import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import {
  opportunitiesService,
  type Comment,
  type CreateCommentBody,
  type CreatePostBody,
  type LikeToggleResult,
  type Post,
  type PostCategory,
  type ReportPostBody,
  type SaveToggleResult,
  type UpdatePostBody,
} from '../../shared/services/opportunities.service';
import type { ApiResponse } from '../../shared/types';

const FEED_KEY = (category: PostCategory | null) =>
  ['opportunities', 'feed', category ?? 'ALL'] as const;
const POST_KEY = (id: string) => ['opportunities', 'post', id] as const;
const SAVED_KEY = ['opportunities', 'saved'] as const;
const COMMENTS_KEY = (postId: string) =>
  ['opportunities', 'comments', postId] as const;

const PAGE_SIZE = 20;

/**
 * Infinite-scroll feed of community posts. `category=null` returns the global
 * feed; passing a value restricts to that category.
 */
export function useOpportunitiesFeed(category: PostCategory | null) {
  const query = useInfiniteQuery({
    queryKey: FEED_KEY(category),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      opportunitiesService.list({
        category: category ?? undefined,
        page: pageParam as number,
        size: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const meta = lastPage.meta.pagination;
      if (!meta) return undefined;
      const nextPage = meta.page + 1;
      return nextPage < meta.totalPages ? nextPage : undefined;
    },
  });

  // Flatten all loaded pages so the page component just iterates one list.
  const posts: Post[] = (query.data?.pages ?? []).flatMap((p) => p.data);

  return {
    posts,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  };
}

/** Single-post hook used by the detail page. Disabled when `id` is null. */
export function useOpportunity(id: string | null) {
  const query = useQuery({
    queryKey: POST_KEY(id ?? ''),
    queryFn: async () => (await opportunitiesService.get(id!)).data,
    enabled: Boolean(id),
  });
  return {
    post: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Phase-2 write mutations bundled in one hook so pages can subscribe with a
 * single import. Every successful mutation invalidates the affected feed +
 * post caches so the next read re-fetches fresh data.
 */
export function useOpportunityMutations() {
  const qc = useQueryClient();

  const invalidateFeeds = () =>
    qc.invalidateQueries({ queryKey: ['opportunities', 'feed'] });

  const create = useMutation({
    mutationFn: async (body: CreatePostBody) =>
      (await opportunitiesService.create(body)).data,
    onSuccess: () => invalidateFeeds(),
  });

  const update = useMutation({
    mutationFn: async (vars: { id: string; body: UpdatePostBody }) =>
      (await opportunitiesService.update(vars.id, vars.body)).data,
    onSuccess: (post) => {
      invalidateFeeds();
      qc.invalidateQueries({ queryKey: POST_KEY(post.id) });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await opportunitiesService.remove(id);
      return id;
    },
    onSuccess: (id) => {
      invalidateFeeds();
      qc.removeQueries({ queryKey: POST_KEY(id) });
    },
  });

  const report = useMutation({
    mutationFn: async (vars: { id: string; body: ReportPostBody }) => {
      await opportunitiesService.report(vars.id, vars.body);
    },
  });

  // ── Phase 3 — optimistic like / save toggles ───────────────────────────
  // The like / save buttons need to feel instantaneous: we flip the cached
  // post immediately (onMutate), let the server respond, then reconcile the
  // authoritative count from the response (onSuccess). On error the previous
  // state is restored (onError) so the icon doesn't lie.

  type Patch = (p: Post) => Post;

  /** Apply a transform to every cached copy of a post — feed pages, single-
   *  post detail, saved-page. Returns a snapshot of the affected caches so
   *  onError can restore them in one shot. */
  const patchEverywhere = (postId: string, patch: Patch) => {
    type FeedCache = InfiniteData<ApiResponse<Post[]>>;

    // 1. Every feed (per category) — they're stored as infinite-query pages.
    const feedQueries = qc.getQueriesData<FeedCache>({
      queryKey: ['opportunities', 'feed'],
    });
    const feedSnapshot = feedQueries.map(([key, data]) => ({ key, data }));
    feedQueries.forEach(([key, data]) => {
      if (!data) return;
      qc.setQueryData<FeedCache>(key, {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data.map((p) => (p.id === postId ? patch(p) : p)),
        })),
      });
    });

    // 2. Detail page cache.
    const detailKey = POST_KEY(postId);
    const detailSnap = qc.getQueryData<Post>(detailKey);
    if (detailSnap) qc.setQueryData<Post>(detailKey, patch(detailSnap));

    // 3. Saved-page cache.
    const savedSnap = qc.getQueryData<FeedCache>(SAVED_KEY);
    if (savedSnap) {
      qc.setQueryData<FeedCache>(SAVED_KEY, {
        ...savedSnap,
        pages: savedSnap.pages.map((page) => ({
          ...page,
          data: page.data.map((p) => (p.id === postId ? patch(p) : p)),
        })),
      });
    }

    return { feedSnapshot, detailSnap, savedSnap };
  };

  const restore = (snapshot: ReturnType<typeof patchEverywhere>, postId: string) => {
    snapshot.feedSnapshot.forEach(({ key, data }) => qc.setQueryData(key, data));
    if (snapshot.detailSnap) qc.setQueryData(POST_KEY(postId), snapshot.detailSnap);
    if (snapshot.savedSnap) qc.setQueryData(SAVED_KEY, snapshot.savedSnap);
  };

  const toggleLike = useMutation({
    mutationFn: async (id: string) =>
      (await opportunitiesService.toggleLike(id)).data,
    onMutate: async (id: string) => {
      // Cancel in-flight queries so they don't overwrite our optimistic edit.
      await qc.cancelQueries({ queryKey: ['opportunities'] });
      const snap = patchEverywhere(id, (p) => ({
        ...p,
        likedByMe: !p.likedByMe,
        likeCount: p.likedByMe ? Math.max(p.likeCount - 1, 0) : p.likeCount + 1,
      }));
      return { snap };
    },
    onError: (_err, id, ctx) => {
      if (ctx?.snap) restore(ctx.snap, id);
    },
    onSuccess: (data: LikeToggleResult, id) => {
      // Reconcile with the authoritative count from the server.
      patchEverywhere(id, (p) => ({
        ...p,
        likedByMe: data.liked,
        likeCount: data.likeCount,
      }));
    },
  });

  const toggleSave = useMutation({
    mutationFn: async (id: string) =>
      (await opportunitiesService.toggleSave(id)).data,
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ['opportunities'] });
      const snap = patchEverywhere(id, (p) => ({
        ...p,
        savedByMe: !p.savedByMe,
        saveCount: p.savedByMe ? Math.max(p.saveCount - 1, 0) : p.saveCount + 1,
      }));
      return { snap };
    },
    onError: (_err, id, ctx) => {
      if (ctx?.snap) restore(ctx.snap, id);
    },
    onSuccess: (data: SaveToggleResult, id) => {
      patchEverywhere(id, (p) => ({
        ...p,
        savedByMe: data.saved,
        saveCount: data.saveCount,
      }));
      // The saved page may need a refresh when a post is unsaved (it should
      // drop out) or saved (it should appear at the top) — the optimistic
      // patch only updates EXISTING rows, it can't insert/remove them.
      qc.invalidateQueries({ queryKey: SAVED_KEY });
    },
  });

  // ── Phase 4 — comment mutations ───────────────────────────────────────

  /** Bump or decrement the comment counter on every cached copy of a post.
   *  Called by the comment create / delete mutations so the badge updates
   *  in lockstep with the thread itself. */
  const patchCommentCount = (postId: string, delta: number) => {
    type FeedCache = InfiniteData<ApiResponse<Post[]>>;
    const bump = (p: Post): Post => ({
      ...p,
      commentCount: Math.max(p.commentCount + delta, 0),
    });
    const feedQueries = qc.getQueriesData<FeedCache>({
      queryKey: ['opportunities', 'feed'],
    });
    feedQueries.forEach(([key, data]) => {
      if (!data) return;
      qc.setQueryData<FeedCache>(key, {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data.map((p) => (p.id === postId ? bump(p) : p)),
        })),
      });
    });
    const detail = qc.getQueryData<Post>(POST_KEY(postId));
    if (detail) qc.setQueryData<Post>(POST_KEY(postId), bump(detail));
    const saved = qc.getQueryData<FeedCache>(SAVED_KEY);
    if (saved) {
      qc.setQueryData<FeedCache>(SAVED_KEY, {
        ...saved,
        pages: saved.pages.map((page) => ({
          ...page,
          data: page.data.map((p) => (p.id === postId ? bump(p) : p)),
        })),
      });
    }
  };

  const createComment = useMutation({
    mutationFn: async (vars: { postId: string; body: CreateCommentBody }) =>
      (await opportunitiesService.createComment(vars.postId, vars.body)).data,
    onSuccess: (comment, vars) => {
      patchCommentCount(vars.postId, +1);
      // Append the new comment to the cached thread so the user sees it
      // appear instantly (the thread is oldest-first).
      qc.setQueryData<InfiniteData<ApiResponse<Comment[]>>>(
        COMMENTS_KEY(vars.postId),
        (prev) => {
          if (!prev || prev.pages.length === 0) return prev;
          const lastIdx = prev.pages.length - 1;
          const lastPage = prev.pages[lastIdx];
          return {
            ...prev,
            pages: prev.pages.map((page, i) =>
              i === lastIdx
                ? { ...page, data: [...lastPage.data, comment] }
                : page,
            ),
          };
        },
      );
    },
  });

  const deleteComment = useMutation({
    mutationFn: async (vars: { commentId: string; postId: string }) => {
      await opportunitiesService.deleteComment(vars.commentId);
      return vars;
    },
    onSuccess: (vars) => {
      patchCommentCount(vars.postId, -1);
      // Drop the row from every page of the cached thread.
      qc.setQueryData<InfiniteData<ApiResponse<Comment[]>>>(
        COMMENTS_KEY(vars.postId),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              data: page.data.filter((c) => c.id !== vars.commentId),
            })),
          };
        },
      );
    },
  });

  return {
    create, update, remove, report,
    toggleLike, toggleSave,
    createComment, deleteComment,
  };
}

/** Paginated comment thread for a post — oldest first. */
export function useComments(postId: string | null) {
  const query = useInfiniteQuery({
    queryKey: COMMENTS_KEY(postId ?? ''),
    initialPageParam: 0,
    enabled: Boolean(postId),
    queryFn: async ({ pageParam }) =>
      opportunitiesService.listComments(postId!, { page: pageParam as number, size: 20 }),
    getNextPageParam: (lastPage) => {
      const meta = lastPage.meta.pagination;
      if (!meta) return undefined;
      const nextPage = meta.page + 1;
      return nextPage < meta.totalPages ? nextPage : undefined;
    },
  });
  const comments: Comment[] = (query.data?.pages ?? []).flatMap((p) => p.data);
  return {
    comments,
    total: query.data?.pages?.[0]?.meta?.pagination?.totalElements ?? comments.length,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  };
}

/**
 * Infinite-scroll list of the current user's saved posts.
 */
export function useSavedOpportunities() {
  const query = useInfiniteQuery({
    queryKey: SAVED_KEY,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      opportunitiesService.listSaved({ page: pageParam as number, size: PAGE_SIZE }),
    getNextPageParam: (lastPage) => {
      const meta = lastPage.meta.pagination;
      if (!meta) return undefined;
      const nextPage = meta.page + 1;
      return nextPage < meta.totalPages ? nextPage : undefined;
    },
  });
  const posts: Post[] = (query.data?.pages ?? []).flatMap((p) => p.data);
  return {
    posts,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  };
}
